import axios from 'axios';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../lib/apiOrigin';

// Domaine d'API direct en production (pas de relais Vercel), relatif en développement
const API_BASE_URL = API_BASE;

// Créer une instance axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  async (config) => {
    // PRIORITÉ 1 : Récupérer le token Supabase (SSO via postMessage ou session existante)
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
        console.log('✅ [API] Using Supabase token for request');
        return config;
      }
    } catch (error) {
      console.log('⚠️ [API] Could not get Supabase token, trying default token');
    }

    // PRIORITÉ 2 : token SSO injecté via URL (?sso_token) dans axios.defaults
    const defaultAuth = axios.defaults.headers.common['Authorization'];
    if (defaultAuth) {
      config.headers.Authorization = defaultAuth;
      console.log('✅ [API] Using SSO URL token for request');
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les réponses ET AUTO-EXTRAIRE LES DONNÉES
api.interceptors.response.use(
  (response) => {
    // Si la réponse a un format { success: true, data: [...] }
    // On retourne directement response.data.data pour simplifier
    if (response.data?.success === true && response.data.data !== undefined && response.data.data !== null) {
      console.log('[API] Auto-extracting data from response');
      return {
        ...response,
        data: response.data.data  // ✅ Extraction automatique !
      };
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      console.log('⚠️ [API] Received 401 error, checking session validity...');

      // Vérifier session Supabase
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          console.log('✅ [API] Supabase session still valid, token might have been rejected by backend');
          return Promise.reject(error);
        }
      } catch (sessionError) {
        console.log('❌ [API] Could not check Supabase session:', sessionError);
      }

      // Vérifier token SSO URL injecté dans axios.defaults
      if (axios.defaults.headers.common['Authorization']) {
        console.log('✅ [API] SSO URL token still present, not redirecting');
        return Promise.reject(error);
      }

      // Aucun token valide — rediriger vers login
      console.log('🚪 [API] No valid session, redirecting to login');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API d'authentification
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
  changePassword: (passwordData) => api.put('/auth/password', passwordData),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
};

// API des utilisateurs
export const usersAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getInvestors: (params) => api.get('/users/investors', { params }),
  getEntrepreneurs: (params) => api.get('/users/entrepreneurs', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  updateInvestorProfile: (id, profileData) => api.put(`/users/${id}/investor-profile`, profileData),
  uploadDocument: (id, documentData) => api.post(`/users/${id}/upload-document`, documentData),
  deleteDocument: (id, docId) => api.delete(`/users/${id}/documents/${docId}`),
  updateAvatar: (id, avatarData) => api.put(`/users/${id}/avatar`, avatarData),
};

// API des projets
export const projectsAPI = {
  getProjects: (params) => api.get('/projects', { params }),
  getFeaturedProjects: () => api.get('/projects/featured'),
  getMyProjects: () => api.get('/projects/my-projects'),
  getProject: (id) => api.get(`/projects/${id}`),
  createProject: (projectData) => api.post('/projects', projectData),
  updateProject: (id, projectData) => api.put(`/projects/${id}`, projectData),
  deleteProject: (id) => api.delete(`/projects/${id}`),
  likeProject: (id) => api.post(`/projects/${id}/like`),
  shareProject: (id) => api.post(`/projects/${id}/share`),
  uploadDocument: (id, documentData) => api.post(`/projects/${id}/upload-document`, documentData),
  uploadImage: (id, imageData) => api.post(`/projects/${id}/upload-image`, imageData),
  getCategoryStats: () => api.get('/projects/categories/stats'),
};

// API des contacts
export const contactsAPI = {
  getContacts: (params) => api.get('/contacts', { params }),
  getReceivedContacts: (params) => api.get('/contacts/received', { params }),
  getSentContacts: (params) => api.get('/contacts/sent', { params }),
  getContact: (id) => api.get(`/contacts/${id}`),
  createContact: (contactData) => api.post('/contacts', contactData),
  respondToContact: (id, responseData) => api.put(`/contacts/${id}/respond`, responseData),
  declineContact: (id, reason) => api.put(`/contacts/${id}/decline`, { reason }),
  addFollowUp: (id, followUpData) => api.post(`/contacts/${id}/follow-up`, followUpData),
  archiveContact: (id) => api.put(`/contacts/${id}/archive`),
  getContactStats: () => api.get('/contacts/stats'),
};

// API des abonnements
export const subscriptionsAPI = {
  getSubscriptions: () => api.get('/subscriptions'),
  getSubscription: (id) => api.get(`/subscriptions/${id}`),
  compareSubscriptions: () => api.get('/subscriptions/compare'),
  getCurrentSubscription: () => api.get('/subscriptions/user/current'),
  upgradeSubscription: (upgradeData) => api.post('/subscriptions/upgrade', upgradeData),
  downgradeSubscription: (downgradeData) => api.post('/subscriptions/downgrade', downgradeData),
  cancelSubscription: () => api.post('/subscriptions/cancel'),
  checkFeature: (feature) => api.get(`/subscriptions/features/${feature}`),
  getUsage: () => api.get('/subscriptions/usage'),
};

// API des paiements
export const paymentsAPI = {
  createPaymentIntent: (paymentData) => api.post('/payments/create-intent', paymentData),
  confirmPayment: (paymentData) => api.post('/payments/confirm', paymentData),
  getPaymentHistory: (params) => api.get('/payments/history', { params }),
  getPayment: (id) => api.get(`/payments/${id}`),
  requestRefund: (refundData) => api.post('/payments/refund', refundData),
};

// API du catalogue de financement
export const financingAPI = {
  getMeta: () => api.get('/financing-products/meta'),
  getProducts: (params) => api.get('/financing-products', { params }),
  getProduct: (id) => api.get(`/financing-products/${id}`),
  // Admin
  adminListProducts: (params) => api.get('/admin/financing-products', { params }),
  adminCreateProduct: (data) => api.post('/admin/financing-products', data),
  adminUpdateProduct: (id, data) => api.put(`/admin/financing-products/${id}`, data),
  adminDeleteProduct: (id) => api.delete(`/admin/financing-products/${id}`),
};

// API des pays
export const countriesAPI = {
  getCountries: (params) => api.get('/countries', { params }),
  getRegions: () => api.get('/countries/regions'),
  getCountry: (code) => api.get(`/countries/${code}`),
  getCountryStats: () => api.get('/countries/stats'),
};

// API des devises
export const currenciesAPI = {
  getCurrencies: (params) => api.get('/currencies', { params }),
  getDefaultCurrency: () => api.get('/currencies/default'),
  getCurrency: (code) => api.get(`/currencies/${code}`),
  convertCurrency: (conversionData) => api.post('/currencies/convert', conversionData),
  getExchangeRates: (params) => api.get('/currencies/rates', { params }),
};

// API générale
export const generalAPI = {
  health: () => api.get('/health'),
};

// API LOGS ET MONITORING
export const logsAPI = {
  getLogs: (params) => api.get('/super-admin/logs', { params }),
  getStats: () => api.get('/super-admin/logs/stats')
};

export default api;
